import {
  CasinoGameTagDialogData,
  ConfirmationDialogData,
  DocumentUploadDialogData,
  InformativeDialogData,
  PromptDisplayStatus,
  SystemBetSelectionOverviewDialogData,
  SystemBetStakeUpdateDialogData,
} from '../../data-types/dialogs/dialogInterfaces';
import { NonUkUserVerificationDialogData } from '../../data-types/international/internationalInterfaces';
import { GhostUserDialogData } from '../../data-types/login/loginInterfaces';
import { QueuedDialog, QueuedNotification } from '../../data-types/notification/notificationInterfaces';
import { ResetState } from './stateTypes';

export type DialogActionType =
  | ResetState
  | SetErrorDialog
  | SetCookieSnackbar
  | SetChatCookieDialog
  | ToggleGhostUserDialog
  | SetRealityCheckDialog
  | EnqueueDialog
  | DequeueDialog
  | EnqueueNotification
  | DequeueNotification
  | SetNonUkUserVerificationDialog
  | SetReviewDialogOpen
  | SetDisplayAppInstallPrompt
  | SetUserConfirmProductUnblockDialog
  | IncreaseLimitConfirmationDialogCount
  | DecreaseLimitConfirmationDialogCount
  | SetConfirmationDialogData
  | SetNetPromoterScoreDialog
  | SetIsInboxOpen
  | SetDocumentUploadData
  | SetInformativeDialogData
  | SetCasinoGameTagDialogData
  | SetSystemBetStakeUpdateDialogQueue
  | SetSystemBetSelectionOverviewDialogData
  | DequeueSystemBetStakeUpdateDialogData
  | SetPostHogSurveyDialog
  | MyPromotionsDialogOpen
  | DequeueAllSystemBetStakeUpdates;

export const SET_ERROR_DIALOG = 'SET_ERROR_DIALOG';
export const TOGGLE_GHOST_USER_DIALOG = 'TOGGLE_GHOST_USER_DIALOG';
export const TOGGLE_GHOST_USER_STREAM_DIALOG = 'TOGGLE_GHOST_USER_STREAM_DIALOG';
export const SET_CHAT_COOKIE_DIALOG = 'SET_CHAT_COOKIE_DIALOG';
export const SET_COOKIE_SNACKBAR = 'SET_COOKIE_SNACKBAR';
export const SET_REALITY_CHECK_DIALOG = 'SET_REALITY_CHECK_DIALOG';
export const ENQUEUE_DIALOG = 'ENQUEUE_DIALOG';
export const DEQUEUE_DIALOG = 'DEQUEUE_DIALOG';
export const ENQUEUE_NOTIFICATION = 'ENQUEUE_NOTIFICATION';
export const DEQUEUE_NOTIFICATION = 'DEQUEUE_NOTIFICATION';
export const SET_NON_UK_USER_VERIFICATION_DIALOG = 'SET_NON_UK_USER_VERIFICATION_DIALOG';
export const SET_REVIEW_DIALOG_OPEN = 'SET_REVIEW_DIALOG_OPEN';
export const SET_DISPLAY_APP_INSTALL_PROMPT = 'SET_DISPLAY_APP_INSTALL_PROMPT';
export const SET_USER_CONFIRM_PRODUCT_UNBLOCK_DIALOG = 'SET_USER_CONFIRM_PRODUCT_UNBLOCK_DIALOG';
export const INCREASE_LIMIT_CONFIRMATION_DIALOG_COUNT = 'INCREASE_LIMIT_CONFIRMATION_DIALOG_COUNT';
export const DECREASE_LIMIT_CONFIRMATION_DIALOG_COUNT = 'DECREASE_LIMIT_CONFIRMATION_DIALOG_COUNT';
export const SET_CONFIRMATION_DIALOG_DATA = 'SET_CONFIRMATION_DIALOG_DATA';
export const SET_NET_PROMOTER_SCORE_DIALOG = 'SET_NET_PROMOTER_SCORE_DIALOG';
export const SET_IS_INBOX_OPEN = 'SET_IS_INBOX_OPEN';
export const SET_DOCUMENT_UPLOAD_DATA = 'SET_DOCUMENT_UPLOAD_DATA';
export const SET_INFORMATIVE_DIALOG_DATA = 'SET_INFORMATIVE_DIALOG_DATA';
export const SET_CASINO_GAME_TAG_DIALOG_DATA = 'SET_CASINO_GAME_TAG_DIALOG_DATA';
export const SET_SYSTEM_BET_STAKE_UPDATE_DIALOG_DATA = 'SET_SYSTEM_BET_STAKE_UPDATE_DIALOG_DATA';
export const SET_SYSTEM_BETS_OVERVIEW_DIALOG_DATA = 'SET_SYSTEM_BETS_OVERVIEW_DIALOG_DATA';
export const DEQUEUE_SYSTEM_BET_STAKE_UPDATE_DATA = 'DEQUEUE_SYSTEM_BET_STAKE_UPDATE_DATA';
export const SET_POST_HOG_SURVEY_DIALOG = 'SET_POST_HOG_SURVEY_DIALOG';
export const SET_MY_PROMOTIONS_DIALOG_OPEN = 'MY_PROMOTIONS_DIALOG_OPEN';
export const DEQUEUE_ALL_SYSTEM_BET_STAKE_UPDATE_DATA = 'DEQUEUE_ALL_SYSTEM_BET_STAKE_UPDATE_DATA';

export interface DialogState {
  generalErrorDialogMessage: string;
  queuedDialogs: QueuedDialog[];
  queuedNotifications: QueuedNotification[];
  ghostUserDialogData: GhostUserDialogData;
  chatCookieDialogOpen: boolean;
  cookieSnackbarOpen: boolean;
  nonUkUserVerificationDialogData: NonUkUserVerificationDialogData;
  realityCheckDialog: boolean;
  promptDisplayStatus: PromptDisplayStatus;
  displayAppInstallPrompt: boolean;
  userProductUnblockConfirmDialog: boolean;
  limitConfirmDialogCount: number;
  confirmationDialog: ConfirmationDialogData;
  netPromoterScoreDialogOpen: boolean;
  isInboxOpen: boolean;
  documentUploadDialogData: DocumentUploadDialogData;
  informativeDialogData: InformativeDialogData;
  casinoGameTagDialogData: CasinoGameTagDialogData;
  systemBetStakeUpdateQueue: SystemBetStakeUpdateDialogData[];
  systemBetSelectionOverviewDialogData: SystemBetSelectionOverviewDialogData;
  postHogSurveyDialogOpen: boolean;
  myPromotionsDialogOpen: boolean;
}

interface SetErrorDialog {
  type: typeof SET_ERROR_DIALOG;
  meta: string;
}

interface EnqueueDialog {
  type: typeof ENQUEUE_DIALOG;
  meta: QueuedDialog;
}

interface DequeueDialog {
  type: typeof DEQUEUE_DIALOG;
}

interface EnqueueNotification {
  type: typeof ENQUEUE_NOTIFICATION;
  meta: QueuedNotification;
}

interface DequeueNotification {
  type: typeof DEQUEUE_NOTIFICATION;
}

interface ToggleGhostUserDialog {
  type: typeof TOGGLE_GHOST_USER_DIALOG;
  meta: GhostUserDialogData;
}

interface SetCookieSnackbar {
  type: typeof SET_COOKIE_SNACKBAR;
  meta: boolean;
}

interface SetChatCookieDialog {
  type: typeof SET_CHAT_COOKIE_DIALOG;
  meta: boolean;
}

interface SetRealityCheckDialog {
  type: typeof SET_REALITY_CHECK_DIALOG;
  meta: boolean;
}

interface SetNonUkUserVerificationDialog {
  type: typeof SET_NON_UK_USER_VERIFICATION_DIALOG;
  meta: NonUkUserVerificationDialogData;
}

interface SetReviewDialogOpen {
  type: typeof SET_REVIEW_DIALOG_OPEN;
  meta: boolean;
}

interface SetDisplayAppInstallPrompt {
  type: typeof SET_DISPLAY_APP_INSTALL_PROMPT;
  meta: boolean;
}

interface SetUserConfirmProductUnblockDialog {
  type: typeof SET_USER_CONFIRM_PRODUCT_UNBLOCK_DIALOG;
  meta: boolean;
}

interface IncreaseLimitConfirmationDialogCount {
  type: typeof INCREASE_LIMIT_CONFIRMATION_DIALOG_COUNT;
}

interface DecreaseLimitConfirmationDialogCount {
  type: typeof DECREASE_LIMIT_CONFIRMATION_DIALOG_COUNT;
}

interface SetNetPromoterScoreDialog {
  type: typeof SET_NET_PROMOTER_SCORE_DIALOG;
  meta: boolean;
}

interface SetIsInboxOpen {
  type: typeof SET_IS_INBOX_OPEN;
  meta: boolean;
}

interface SetDocumentUploadData {
  type: typeof SET_DOCUMENT_UPLOAD_DATA;
  meta: DocumentUploadDialogData;
}

interface SetConfirmationDialogData {
  type: typeof SET_CONFIRMATION_DIALOG_DATA;
  meta: ConfirmationDialogData;
}

interface SetInformativeDialogData {
  type: typeof SET_INFORMATIVE_DIALOG_DATA;
  meta: InformativeDialogData;
}

interface SetCasinoGameTagDialogData {
  type: typeof SET_CASINO_GAME_TAG_DIALOG_DATA;
  meta: CasinoGameTagDialogData;
}

interface SetSystemBetStakeUpdateDialogQueue {
  type: typeof SET_SYSTEM_BET_STAKE_UPDATE_DIALOG_DATA;
  meta: SystemBetStakeUpdateDialogData;
}
interface DequeueSystemBetStakeUpdateDialogData {
  type: typeof DEQUEUE_SYSTEM_BET_STAKE_UPDATE_DATA;
}

interface SetSystemBetSelectionOverviewDialogData {
  type: typeof SET_SYSTEM_BETS_OVERVIEW_DIALOG_DATA;
  meta: SystemBetSelectionOverviewDialogData;
}

interface SetPostHogSurveyDialog {
  type: typeof SET_POST_HOG_SURVEY_DIALOG;
  meta: boolean;
}

interface MyPromotionsDialogOpen {
  type: typeof SET_MY_PROMOTIONS_DIALOG_OPEN;
  meta: boolean;
}

interface DequeueAllSystemBetStakeUpdates {
  type: typeof DEQUEUE_ALL_SYSTEM_BET_STAKE_UPDATE_DATA;
}
